// ─────────────────────────────────────────────────────────────────────────────
// DesktopLogin, the sign-in gate for the desktop React app (/app-react).
//
// WHY THIS EXISTS: app-react.html mounts <App/> with NO auth gate, a logged-out
// user hit a broken shell (every API 401s) with no way to sign in. Legacy login
// lived only on /app.html. This is the React equivalent of MobileLogin, on the
// SAME proven endpoints, so /app-react can authenticate on its own (required for
// the legacy→React desktop switch).
//
// Flows (1:1 with MobileLogin / the legacy login):
//   • email + password   → POST /api/login
//   • emailed OTP         → code:'otp_required' → POST /api/login/verify-otp
//   • forgot password     → POST /api/forgot-password (neutral confirmation)
//   • passkey (Touch ID)  → EMAIL-FIRST: POST /api/webauthn/auth-options {email}
//                           → navigator.credentials.get → POST /api/webauthn/auth.
//                           Email-first (not discoverable like mobile) because a
//                           Mac Secure-Enclave passkey is device-bound, so the
//                           worker must send its credential id, restored from the
//                           legacy app.html flow (passkey-desktop 2026-07-05).
//   • post-login enroll    → after a password/OTP login, if the account has no
//                           passkey (server-side /api/webauthn/check, never a
//                           device flag) offer to set one up; "Not now" suppresses
//                           for the session only.
// On success: setToken() (writes cntrl_auth_token) → onAuthed() flips <App/> to
// the routed app with no reload. Account creation lives on the marketing web
// (Create account → /signup.html).
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useRef, useState } from 'react';
import type { JSX } from 'react';
import { AUTH_WORKER, authHeaders, setToken } from '../lib';
import { passkeySupported, passkeyGet, passkeyCreate, isPasskeyCancel } from '../mobile/lib/webauthn';
import './DesktopLogin.css';

interface LoginResponse {
  token?: string;
  code?: string; // 'otp_required' | 'payment_required'
  login_token?: string;
  email?: string;
  error?: string;
}

function AcMark(): JSX.Element {
  return (
    <svg viewBox="0 0 317.48 317.48" width="40" height="40" aria-hidden="true">
      <polygon
        fill="currentColor"
        points="269 144.98 317.48 96.5 317.48 0 207.22 0 158.74 48.48 110.26 0 0 0 0 110.26 48.48 158.74 48.48 158.74 48.48 158.74 0 207.22 0 317.48 110.26 317.48 158.74 269 207.22 317.48 317.48 317.48 317.48 220.99 269 172.5 269 172.19 195.65 172.19 195.65 195.34 158.9 195.34 122.14 195.34 122.14 158.58 122.14 121.83 158.9 121.83 195.65 121.83 195.65 144.98 269 144.98"
      />
    </svg>
  );
}

export function DesktopLogin({ onAuthed }: { readonly onAuthed: () => void }): JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [hint, setHint] = useState(false); // OTP prompt uses a muted tone
  const [busy, setBusy] = useState(false);
  const [pendingOtp, setPendingOtp] = useState<string | null>(null); // login_token
  const [forgotLabel, setForgotLabel] = useState('Forgot password?');
  // otp-resend 2026-07-06: resend the sign-in code with a 30s cooldown.
  const [resendCooldown, setResendCooldown] = useState(0);
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = window.setTimeout(() => setResendCooldown((n) => n - 1), 1000);
    return () => window.clearTimeout(id);
  }, [resendCooldown]);
  function resendOtp(): void {
    if (!pendingOtp || resendCooldown > 0) return;
    setResendCooldown(30);
    fetch(AUTH_WORKER + '/api/login/resend-otp', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ login_token: pendingOtp }),
    })
      .then((r) => r.json().then((d: LoginResponse) => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        setHint(true);
        setError(ok ? 'New code sent, check your email.' : (d.error || 'Could not resend, try again.'));
        if (!ok) setResendCooldown(0);
      })
      .catch(() => { setHint(false); setError('Network error, try again.'); setResendCooldown(0); });
  }
  const passRef = useRef<HTMLInputElement>(null);
  const inOtp = !!pendingOtp;
  // passkey-desktop 2026-07-05
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [enrollOffer, setEnrollOffer] = useState<string | null>(null); // token pending enroll decision
  const [enrollBusy, setEnrollBusy] = useState(false);
  const canPasskey = passkeySupported();

  // No data load fires on this screen, so the host page's boot loader (#acLoader,
  // hidden by lib/dataCache on first data settle) would otherwise sit on top of
  // the form for up to 12s. Dismiss it as soon as the login renders.
  useEffect(() => {
    const w = window as unknown as { acHideLoader?: () => void };
    if (typeof w.acHideLoader === 'function') w.acHideLoader();
  }, []);

  function finish(token: string): void {
    setToken(token);
    onAuthed();
  }

  // After a password/OTP login: offer Touch ID set-up while the ACCOUNT has no
  // passkey (server truth via /api/webauthn/check, mirrors the MobileLogin
  // gate fix; a device-local "already offered" flag permanently stranded
  // devices that dismissed once). "Not now" suppresses for this session only.
  async function finishWithEnrollOffer(token: string): Promise<void> {
    if (!canPasskey) { finish(token); return; }
    let dismissed = false;
    try { dismissed = sessionStorage.getItem('ac:passkey:dismissed') === '1'; } catch { /* ignore */ }
    if (dismissed) { finish(token); return; }
    try {
      const em = email.trim().toLowerCase();
      const j = await fetch(
        AUTH_WORKER + '/api/webauthn/check?email=' + encodeURIComponent(em)
      ).then((r) => r.json());
      if (j && j.hasCredential) { finish(token); return; } // enrolled, never nag
      setToken(token); // register-options authenticates via Bearer
      setEnrollOffer(token);
    } catch {
      finish(token); // check failed, sign in normally, offer again next login
    }
  }

  function dismissEnroll(): void {
    try { sessionStorage.setItem('ac:passkey:dismissed', '1'); } catch { /* ignore */ }
    finish(enrollOffer || '');
  }

  async function enrollPasskey(): Promise<void> {
    const token = enrollOffer || '';
    setEnrollBusy(true);
    try {
      const optRes = await fetch(AUTH_WORKER + '/api/webauthn/register-options', {
        method: 'POST',
        headers: authHeaders('POST'),
      }).then((r) => r.json());
      if (!optRes || !optRes.success || !optRes.options) throw new Error('setup-failed');
      const attestation = await passkeyCreate(optRes.options);
      await fetch(AUTH_WORKER + '/api/webauthn/register', {
        method: 'POST',
        headers: authHeaders('POST'),
        body: JSON.stringify(attestation),
      }).then((r) => r.json());
      setEnrollBusy(false);
      finish(token); // enrolled, the server-side check gates future offers
    } catch (err) {
      setEnrollBusy(false);
      try { sessionStorage.setItem('ac:passkey:dismissed', '1'); } catch { /* ignore */ }
      if (!isPasskeyCancel(err)) { /* best-effort; the user is still signed in */ }
      finish(token);
    }
  }

  // Touch ID sign-in, email-first: a Mac Secure-Enclave passkey is device-bound
  // (not discoverable), so the worker needs the email to return the credential id.
  async function doPasskeyLogin(): Promise<void> {
    const em = email.trim().toLowerCase();
    if (!em) {
      setHint(true);
      setError('Enter your email, then use Touch ID.');
      return;
    }
    setError(''); setHint(false); setPasskeyBusy(true);
    try {
      const optRes = await fetch(AUTH_WORKER + '/api/webauthn/auth-options', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: em }),
      }).then((r) => r.json());
      if (!optRes || !optRes.success || !optRes.hasCredential || !optRes.options) {
        setPasskeyBusy(false);
        setError('No Touch ID set up for that email on this device, use your password.');
        return;
      }
      const assertion = await passkeyGet(optRes.options);
      const authRes = await fetch(AUTH_WORKER + '/api/webauthn/auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: em, response: assertion }),
      }).then((r) => r.json());
      setPasskeyBusy(false);
      if (authRes && authRes.success && authRes.token) {
        finish(authRes.token); // has a passkey by definition → no enroll offer
      } else {
        setError((authRes && authRes.error) || 'Touch ID sign-in failed, use your password.');
      }
    } catch (err) {
      setPasskeyBusy(false);
      if (isPasskeyCancel(err)) { setError(''); return; } // user closed the sheet
      if ((err as { name?: string })?.name === 'SecurityError') {
        setError('Passkeys only work on artistcntrl.com, not the preview domain.');
      } else {
        setError('Touch ID sign-in didn’t complete, use your password.');
      }
    }
  }

  function login(): void {
    const em = email.trim().toLowerCase();
    const pw = password;
    setError('');
    setHint(false);
    if (!em || !pw) {
      setError('Email and password required');
      return;
    }
    setBusy(true);
    fetch(AUTH_WORKER + '/api/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: em, password: pw }),
    })
      .then((r) => r.json().then((d: LoginResponse) => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        setBusy(false);
        if (ok && d.token) {
          void finishWithEnrollOffer(d.token);
        } else if (d.code === 'otp_required') {
          setPendingOtp(d.login_token || '');
          setPassword('');
          setHint(true);
          setError('Enter the 6-digit code we emailed to ' + (d.email || em) + '.');
          setTimeout(() => passRef.current?.focus(), 60);
        } else if (d.code === 'payment_required') {
          setError('Activate your subscription on the web first.');
        } else {
          setError(d.error || 'Wrong email or password');
          setPassword('');
        }
      })
      .catch(() => {
        setBusy(false);
        setError('Network error, try again.');
      });
  }

  function verifyOtp(): void {
    const code = (password || '').replace(/\D/g, '');
    if (code.length !== 6) {
      setHint(false);
      setError('Enter the 6-digit code.');
      return;
    }
    setBusy(true);
    fetch(AUTH_WORKER + '/api/login/verify-otp', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ login_token: pendingOtp, code }),
    })
      .then((r) => r.json().then((d: LoginResponse) => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        setBusy(false);
        if (ok && d.token) void finishWithEnrollOffer(d.token);
        else {
          setHint(false);
          setError(d.error || 'Incorrect code, try again.');
          setPassword('');
        }
      })
      .catch(() => {
        setBusy(false);
        setHint(false);
        setError('Network error, try again.');
      });
  }

  function onSubmit(e: React.FormEvent): void {
    e.preventDefault();
    if (inOtp) verifyOtp();
    else login();
  }

  function onForgot(): void {
    let em = email.trim().toLowerCase();
    if (!em) {
      em = (window.prompt('Email to reset:') || '').trim().toLowerCase();
      if (!em) return;
    }
    if (em.indexOf('@') < 0) {
      setHint(false);
      setError('Enter a valid email');
      return;
    }
    setForgotLabel('Sending…');
    fetch(AUTH_WORKER + '/api/forgot-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: em }),
    })
      .catch(() => {})
      .finally(() => {
        setForgotLabel('Check your email ✓');
        setTimeout(() => setForgotLabel('Forgot password?'), 4000);
      });
  }

  if (enrollOffer) {
    return (
      <div className="dlogin">
        <div className="dlogin__card">
          <span className="dlogin__mark">
            <AcMark />
          </span>
          <h1 className="dlogin__title">Set up Touch ID?</h1>
          <p className="dlogin__sub">Sign in next time without a password or emailed code.</p>
          <button className="dlogin__submit" type="button" onClick={() => void enrollPasskey()} disabled={enrollBusy}>
            {enrollBusy ? 'Setting up…' : 'Enable Touch ID'}
          </button>
          <div className="dlogin__foot">
            <button type="button" className="dlogin__link" onClick={dismissEnroll} disabled={enrollBusy}>
              Not now
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dlogin">
      <form className="dlogin__card" onSubmit={onSubmit}>
        <span className="dlogin__mark">
          <AcMark />
        </span>
        <h1 className="dlogin__title">Welcome back</h1>
        <p className="dlogin__sub">Sign in to Artist CNTRL</p>

        <label className="dlogin__field">
          <span className="dlogin__label">Email</span>
          <input
            className="dlogin__input"
            type="email"
            name="email"
            autoComplete="username"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={inOtp}
            placeholder="you@band.com"
          />
        </label>

        <label className="dlogin__field">
          <span className="dlogin__label">{inOtp ? '6-digit code' : 'Password'}</span>
          <input
            ref={passRef}
            className="dlogin__input"
            type={inOtp ? 'text' : 'password'}
            name="password"
            autoComplete={inOtp ? 'one-time-code' : 'current-password'}
            inputMode={inOtp ? 'numeric' : undefined}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={inOtp ? '-,,,,,' : '••••••••'}
          />
        </label>

        {error && <p className={'dlogin__msg' + (hint ? ' dlogin__msg--hint' : '')}>{error}</p>}

        <button className="dlogin__submit" type="submit" disabled={busy}>
          {busy ? 'Signing in…' : inOtp ? 'Verify code' : 'Sign in'}
        </button>

        {canPasskey && !inOtp && (
          <button
            className="dlogin__passkey"
            type="button"
            onClick={() => void doPasskeyLogin()}
            disabled={busy || passkeyBusy}
          >
            {passkeyBusy ? 'Waiting for Touch ID…' : 'Sign in with Touch ID'}
          </button>
        )}

        <div className="dlogin__foot">
          {inOtp ? (
            <button type="button" className="dlogin__link" onClick={resendOtp} disabled={resendCooldown > 0}>
              {resendCooldown > 0 ? 'Resend code in ' + resendCooldown + 's' : 'Resend code'}
            </button>
          ) : (
            <button type="button" className="dlogin__link" onClick={onForgot} disabled={busy}>
              {forgotLabel}
            </button>
          )}
          {/* apply-link 2026-07-09 (Ryan): /signup.html is the INVITE-token flow
              and renders "Sign-up link is invalid" without a token. Public
              account creation goes through the APPLICATION, no bypass. */}
          <a className="dlogin__link" href="/apply.html">
            Apply for an account
          </a>
        </div>
      </form>
    </div>
  );
}
